/*
 * The operator login (API-15, flows/admin-auth): a plain centred card, no side nav. On success the
 * admin token is adopted by the auth context and the operator is routed — to the forced reset when
 * the account still owes one, otherwise to the Contests landing. A refusal shows its distinct copy
 * (bad credentials vs inactive account) keyed off the response code. An operator who is already
 * signed in is redirected away from the login screen.
 */
import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Banner, Button, Card, Input } from '@cou/admin-ui';
import { useAdminAuth } from '../session/admin-auth-context';
import { login as defaultLogin } from '../auth/admin-auth-client';
import { refusalMessage } from '../auth/refusal-copy';
import { AuthLayout } from './AuthLayout';
import { LANDING_SECTION } from '../shell/nav-sections';

export interface LoginScreenProps {
  /** Injected for tests; defaults to the real API-15 login call. */
  readonly login?: typeof defaultLogin;
}

export function LoginScreen({ login = defaultLogin }: LoginScreenProps = {}) {
  const { claims, signIn } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (claims !== null) {
    return <Navigate to={claims.mustResetPw ? '/reset-password' : LANDING_SECTION.path} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result.ok) {
        signIn(result.adminToken);
        void navigate(result.mustResetPw ? '/reset-password' : LANDING_SECTION.path, {
          replace: true,
        });
      } else {
        setError(refusalMessage(result.code));
      }
    } catch {
      setError(refusalMessage(undefined));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <Card variant="frame" eyebrow="Caesars Over/Under" title="Operator sign in">
        <form className="flex flex-col gap-4" onSubmit={(event) => void onSubmit(event)} noValidate>
          {error && (
            <Banner variant="danger" role="alert">
              {error}
            </Banner>
          )}
          <Input
            label="Email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </Card>
    </AuthLayout>
  );
}
